import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Shield, 
  Mail, 
  User, 
  MessageSquare, 
  ArrowLeft,
  CheckCircle2,
  Clock,
  Eye,
  Trash2,
  Loader2,
  Bug,
  Lightbulb,
  HelpCircle,
  MessageCircle,
  Filter,
  RefreshCw,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Users,
  Send,
  Reply
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_CONFIG = {
  general: { icon: MessageCircle, label: "General", color: "text-blue-500" },
  support: { icon: HelpCircle, label: "Support", color: "text-purple-500" },
  bug: { icon: Bug, label: "Bug", color: "text-red-500" },
  feature: { icon: Lightbulb, label: "Feature", color: "text-yellow-500" },
};

const STATUS_CONFIG = {
  unread: { label: "Unread", color: "bg-blue-500/10 text-blue-500 border-blue-500/20" },
  read: { label: "Read", color: "bg-yellow-500/10 text-yellow-500 border-yellow-500/20" },
  resolved: { label: "Resolved", color: "bg-green-500/10 text-green-500 border-green-500/20" },
};

export default function AdminDashboard({ user }) {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedContact, setSelectedContact] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [deleteContactId, setDeleteContactId] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  
  // Reply state
  const [isReplyOpen, setIsReplyOpen] = useState(false);
  const [replyMessage, setReplyMessage] = useState("");
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [replyContact, setReplyContact] = useState(null);

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error("Admin access required");
      navigate("/");
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [contactsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/contacts${filterStatus !== "all" ? `?status=${filterStatus}` : ""}`),
        axios.get(`${API}/admin/contacts/stats`)
      ]);
      setContacts(contactsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/");
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) {
      fetchData();
    }
  }, [filterStatus]);

  const sendReply = async () => {
    if (!replyMessage.trim()) {
      toast.error("Please enter a reply message");
      return;
    }
    
    setIsSendingReply(true);
    try {
      await axios.post(`${API}/admin/contacts/${replyContact.id}/reply`, {
        message: replyMessage.trim()
      });
      toast.success("Reply sent successfully!");
      setIsReplyOpen(false);
      setReplyMessage("");
      setReplyContact(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reply");
    } finally {
      setIsSendingReply(false);
    }
  };

  const openReplyDialog = (contact) => {
    setReplyContact(contact);
    setReplyMessage("");
    setIsReplyOpen(true);
  };

  const updateStatus = async (contactId, newStatus) => {
    setIsUpdating(true);
    try {
      await axios.patch(`${API}/admin/contacts/${contactId}`, {
        status: newStatus,
        admin_notes: adminNotes || null
      });
      toast.success(`Marked as ${newStatus}`);
      fetchData();
      setIsDetailOpen(false);
      setSelectedContact(null);
      setAdminNotes("");
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setIsUpdating(false);
    }
  };

  const deleteContact = async (contactId) => {
    try {
      await axios.delete(`${API}/admin/contacts/${contactId}`);
      toast.success("Contact deleted");
      fetchData();
      setDeleteContactId(null);
    } catch (error) {
      toast.error("Failed to delete contact");
    }
  };

  const openDetail = (contact) => {
    setSelectedContact(contact);
    setAdminNotes(contact.admin_notes || "");
    setIsDetailOpen(true);
    // Auto-mark as read if unread
    if (contact.status === "unread") {
      updateStatus(contact.id, "read");
    }
  };

  if (!user?.is_admin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card/50">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <Link to="/">
            <Button variant="ghost" size="sm" className="mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Scanner
            </Button>
          </Link>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="w-10 h-10 text-primary" />
              <div>
                <h1 className="text-2xl font-bold">Admin Dashboard</h1>
                <p className="text-muted-foreground">Manage contact submissions</p>
              </div>
            </div>
            <Button variant="outline" onClick={fetchData} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total</p>
                    <p className="text-3xl font-bold">{stats.total}</p>
                  </div>
                  <Mail className="w-8 h-8 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-blue-500/20">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-500">Unread</p>
                    <p className="text-3xl font-bold text-blue-500">{stats.unread}</p>
                  </div>
                  <AlertCircle className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-yellow-500/20">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-yellow-500">Read</p>
                    <p className="text-3xl font-bold text-yellow-500">{stats.read}</p>
                  </div>
                  <Eye className="w-8 h-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-green-500/20">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-500">Resolved</p>
                    <p className="text-3xl font-bold text-green-500">{stats.resolved}</p>
                  </div>
                  <CheckCircle2 className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filter */}
        <div className="flex items-center gap-4 mb-6">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Messages</SelectItem>
              <SelectItem value="unread">Unread</SelectItem>
              <SelectItem value="read">Read</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Contact List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : contacts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Mail className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No contact submissions found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {contacts.map((contact) => {
              const CategoryIcon = CATEGORY_CONFIG[contact.category]?.icon || MessageCircle;
              const categoryConfig = CATEGORY_CONFIG[contact.category] || CATEGORY_CONFIG.general;
              const statusConfig = STATUS_CONFIG[contact.status] || STATUS_CONFIG.unread;
              const isExpanded = expandedId === contact.id;

              return (
                <Card 
                  key={contact.id} 
                  className={`transition-all ${contact.status === "unread" ? "border-blue-500/30 bg-blue-500/5" : ""}`}
                  data-testid="contact-card"
                >
                  <CardContent className="pt-4">
                    <div 
                      className="flex items-start gap-4 cursor-pointer"
                      onClick={() => setExpandedId(isExpanded ? null : contact.id)}
                    >
                      <div className={`p-2 rounded-lg bg-secondary ${categoryConfig.color}`}>
                        <CategoryIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium truncate">{contact.name}</span>
                          <Badge variant="outline" className={statusConfig.color}>
                            {statusConfig.label}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            {categoryConfig.label}
                          </Badge>
                        </div>
                        <p className="text-sm font-medium text-foreground mb-1">{contact.subject}</p>
                        <p className="text-xs text-muted-foreground">{contact.email}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {new Date(contact.timestamp).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <div className="bg-muted/50 rounded-lg p-4 mb-4">
                          <p className="text-sm whitespace-pre-wrap">{contact.message}</p>
                        </div>
                        
                        {/* Previous Replies */}
                        {contact.replies && contact.replies.length > 0 && (
                          <div className="mb-4 space-y-2">
                            <p className="text-xs text-muted-foreground font-medium">Previous Replies:</p>
                            {contact.replies.map((r, idx) => (
                              <div key={r.id || idx} className="bg-green-500/10 rounded-lg p-3 border border-green-500/20">
                                <div className="flex items-center gap-2 mb-2">
                                  <Reply className="w-3 h-3 text-green-500" />
                                  <span className="text-xs text-green-500">{r.sent_by_name}</span>
                                  <span className="text-xs text-muted-foreground">
                                    {new Date(r.timestamp).toLocaleString()}
                                  </span>
                                  {r.email_sent && <Badge className="text-xs bg-green-500/20 text-green-400">Sent</Badge>}
                                </div>
                                <p className="text-sm whitespace-pre-wrap">{r.message}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {contact.admin_notes && (
                          <div className="bg-yellow-500/10 rounded-lg p-3 mb-4">
                            <p className="text-xs text-yellow-500 font-medium mb-1">Admin Notes:</p>
                            <p className="text-sm text-yellow-200">{contact.admin_notes}</p>
                          </div>
                        )}
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button 
                            size="sm" 
                            variant="default"
                            onClick={(e) => { e.stopPropagation(); openReplyDialog(contact); }}
                            data-testid="reply-button"
                          >
                            <Send className="w-4 h-4 mr-1" />
                            Reply via Email
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); openDetail(contact); }}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View & Update
                          </Button>
                          {contact.status !== "resolved" && (
                            <Button 
                              size="sm" 
                              variant="secondary"
                              onClick={(e) => { e.stopPropagation(); updateStatus(contact.id, "resolved"); }}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Mark Resolved
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={(e) => { e.stopPropagation(); setDeleteContactId(contact.id); }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Contact Details</DialogTitle>
            <DialogDescription>
              Review and manage this submission
            </DialogDescription>
          </DialogHeader>
          {selectedContact && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">From</p>
                  <p className="font-medium">{selectedContact.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedContact.email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Category</p>
                  <Badge variant="secondary">
                    {CATEGORY_CONFIG[selectedContact.category]?.label || "General"}
                  </Badge>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Subject</p>
                <p className="font-medium">{selectedContact.subject}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-2">Message</p>
                <ScrollArea className="h-[200px] bg-muted/50 rounded-lg p-4">
                  <p className="text-sm whitespace-pre-wrap">{selectedContact.message}</p>
                </ScrollArea>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-2">Admin Notes</p>
                <Textarea
                  placeholder="Add notes about this submission..."
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => updateStatus(selectedContact?.id, "unread")} disabled={isUpdating}>
              Mark Unread
            </Button>
            <Button variant="outline" onClick={() => updateStatus(selectedContact?.id, "read")} disabled={isUpdating}>
              Mark Read
            </Button>
            <Button onClick={() => updateStatus(selectedContact?.id, "resolved")} disabled={isUpdating}>
              {isUpdating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Mark Resolved
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteContactId} onOpenChange={() => setDeleteContactId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Contact?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this contact submission.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteContact(deleteContactId)} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reply Dialog */}
      <Dialog open={isReplyOpen} onOpenChange={setIsReplyOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-primary" />
              Reply to Contact
            </DialogTitle>
            <DialogDescription>
              Send an email reply to this submission
            </DialogDescription>
          </DialogHeader>
          {replyContact && (
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{replyContact.name}</span>
                  <span className="text-sm text-muted-foreground">{replyContact.email}</span>
                </div>
                <p className="text-sm text-muted-foreground mb-2">Subject: {replyContact.subject}</p>
                <div className="border-t border-border pt-2 mt-2">
                  <p className="text-xs text-muted-foreground mb-1">Original Message:</p>
                  <p className="text-sm whitespace-pre-wrap line-clamp-4">{replyContact.message}</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="reply-message">Your Reply</Label>
                <Textarea
                  id="reply-message"
                  placeholder="Type your reply message here..."
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  rows={6}
                  className="resize-none"
                  data-testid="reply-message-input"
                />
                <p className="text-xs text-muted-foreground text-right">
                  {replyMessage.length}/5000 characters
                </p>
              </div>
              
              <div className="bg-blue-500/10 rounded-lg p-3 text-sm text-blue-400">
                <p>This reply will be sent to <strong>{replyContact.email}</strong> and the submission will be marked as resolved.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReplyOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={sendReply} 
              disabled={isSendingReply || !replyMessage.trim()}
              data-testid="send-reply-button"
            >
              {isSendingReply ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Reply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
